import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  TextField,
  Chip,
  Radio,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  InputAdornment,
  Collapse,
  Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NearMeIcon from "@mui/icons-material/NearMe";
import BusinessIcon from "@mui/icons-material/Business";
import FactoryIcon from "@mui/icons-material/Factory";
import ConstructionIcon from "@mui/icons-material/Construction";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_TRADES } from "../../constant/Apipath";
import { BIBlue, BIGrey, lightenHexColor } from "../../assets/helpers";
import SearchMap from "../sites/SearchMap";
import Getcurrentlocation from "../common/Getcurrentlocation";
import { getAddress } from "../../request/Fetchcurrentaddress";
import { English } from "../../constant/EnglishFallback";

const CompanyListFilterModal = ({
  open,
  onClose,
  initialFilters = {},
  onApplyFilters
}) => {
  const { multiLanguageData, loginData } = useSelector((state) => state?.main);
  const LanguageData = multiLanguageData?.Companies || {};

  const {
    TEXT_FILTERS = "Filters",
    BUTTON_APPLY = "Apply",
    BUTTON_CLEARALL = "Clear all",
    TEXT_TRADE = "Trade",
    TEXT_COMPANY = "Company Type",
    BUTTON_SEARCH = "Search"
  } = LanguageData;

  // Local draft states (Draft & Commit pattern)
  const [draftCompanyType, setDraftCompanyType] = useState(
    initialFilters?.companyType || "all"
  );
  const [draftTrade, setDraftTrade] = useState(
    initialFilters?.selectedTrade || null
  );
  const [draftAddress, setDraftAddress] = useState(
    initialFilters?.address || ""
  );
  const [draftAddressDetails, setDraftAddressDetails] = useState(
    initialFilters?.addressDetails || null
  );

  // Accordion state for Trade section
  const [tradeAccordionOpen, setTradeAccordionOpen] = useState(false);

  // Trade search & pagination states
  const [tradeList, setTradeList] = useState([]);
  const [tradeSearchText, setTradeSearchText] = useState("");
  const [tradePageNr, setTradePageNr] = useState(1);
  const [isTradeLoading, setIsTradeLoading] = useState(false);
  const [isTradeSearching, setIsTradeSearching] = useState(false);
  const [hasMoreTrades, setHasMoreTrades] = useState(true);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const appliedTradeSearchRef = useRef("");

  // Synchronize draft states when modal opens
  useEffect(() => {
    if (open) {
      setDraftCompanyType(initialFilters?.companyType || "all");
      setDraftTrade(initialFilters?.selectedTrade || null);
      setDraftAddress(initialFilters?.address || "");
      setDraftAddressDetails(initialFilters?.addressDetails || null);
      setTradeSearchText("");
      appliedTradeSearchRef.current = "";
      // Expand trade accordion if trade is selected
      if (initialFilters?.selectedTrade) {
        setTradeAccordionOpen(true);
      }
    }
  }, [open, initialFilters]);

  // Fetch trades from API
  const fetchTrades = useCallback(
    async (search = "", page = 1, append = false, isUserSearch = false) => {
      const searchList = search?.trim() ? [{ name: search.trim() }] : [{}];
      try {
        setIsTradeLoading(true);
        if (isUserSearch) {
          setIsTradeSearching(true);
        }
        if (!append) {
          setTradeList([]);
        }
        const payload = {
          PageNr: page,
          FullSearch:"",
          SearchList: searchList,
          IncludeRecordNr: false,
          NrOfRecPerPage: 20,
          TypeOfObjectReturned: "NameOnly",
          FetchAllowedRecordsOnly: true
        };
        const res = await Postrequestcall(
          FETCH_TRADES,
          payload,
          loginData?.accessToken
        );
        const resData = res?.data?.data || [];
        const formatted = resData.map((item) => ({
          id: item.id,
          value: item.id,
          label: item.name || item.typeName || item.tradeName || "",
          name: item.name || item.typeName || item.tradeName || ""
        }));

        if (append) {
          setTradeList((prev) => [...prev, ...formatted]);
        } else {
          setTradeList(formatted);
        }
        setTradePageNr(page);
        setHasMoreTrades(formatted.length === 20);
      } catch (err) {
        console.error("fetchTrades -> error:", err);
      } finally {
        setIsTradeLoading(false);
        setIsTradeSearching(false);
      }
    },
    [loginData]
  );

  // Fetch initial trades when trade accordion is opened
  useEffect(() => {
    if (open && tradeAccordionOpen) {
      fetchTrades(appliedTradeSearchRef.current || "", 1, false, false);
    }
  }, [open, tradeAccordionOpen, fetchTrades]);

  // Search trades on button click or enter
  const handleTradeSearch = (searchVal = tradeSearchText) => {
    appliedTradeSearchRef.current = searchVal;
    fetchTrades(searchVal, 1, false, true);
  };

  // Clear trade search
  const handleClearTradeSearch = () => {
    setTradeSearchText("");
    appliedTradeSearchRef.current = "";
    fetchTrades("", 1, false, false);
  };

  const handleTradeSearchChange = (e) => {
    const val = e.target.value;
    setTradeSearchText(val);
    if (!val && appliedTradeSearchRef.current) {
      appliedTradeSearchRef.current = "";
      fetchTrades("", 1, false, false);
    }
  };

  // Handle Trade infinite scroll / load more
  const handleLoadMoreTrades = () => {
    if (!isTradeLoading && hasMoreTrades) {
      fetchTrades(appliedTradeSearchRef.current, tradePageNr + 1, true);
    }
  };

  // Single-select toggle behavior for Trade
  const handleTradeToggle = (trade) => {
    if (
      draftTrade &&
      (draftTrade.id === trade.id || draftTrade.value === trade.value)
    ) {
      // Toggle off / deselect
      setDraftTrade(null);
    } else {
      // Select new trade
      setDraftTrade({
        id: trade.id,
        value: trade.value,
        label: trade.label,
        name: trade.name
      });
    }
  };

  // Google Places place selection
  const handlePlaceSelect = (place) => {
    const lat = Number(
      typeof place?.geometry?.location?.lat === "function"
        ? place.geometry.location.lat()
        : place?.geometry?.location?.lat ?? 0
    );
    const lng = Number(
      typeof place?.geometry?.location?.lng === "function"
        ? place.geometry.location.lng()
        : place?.geometry?.location?.lng ?? 0
    );

    let address = "";
    let city = "";
    let province = "";
    let country = "";

    if (place?.address_components?.length > 0) {
      for (let i = 0; i < place.address_components.length; i++) {
        const comp = place.address_components[i];
        if (comp.types.includes("street_number")) address += comp.long_name + " ";
        if (comp.types.includes("route")) address += comp.long_name + " ";
        if (comp.types.includes("locality") || comp.types.includes("postal_town")) {
          city = comp.long_name;
        }
        if (comp.types.includes("administrative_area_level_1")) {
          province = comp.short_name;
        }
        if (comp.types.includes("country")) {
          country = comp.long_name;
        }
      }
    }

    const formattedAddress =
      place?.formatted_address ||
      (city ? `${city}${province ? `, ${province}` : ""}` : address.trim());

    setDraftAddress(formattedAddress);
    setDraftAddressDetails({
      city,
      province,
      country,
      latitude: lat,
      longitude: lng,
      formattedAddress
    });
  };

  // Clear Address
  const handleClearAddress = () => {
    setDraftAddress("");
    setDraftAddressDetails(null);
  };

  // GPS "Use Current Location"
  const handleCurrentLocationClick = async () => {
    try {
      setIsGettingLocation(true);
      const loc = await Getcurrentlocation(multiLanguageData);
      if (loc && !loc.default && loc.lat && loc.lng) {
        const addressData = await getAddress({ lat: loc.lat, lng: loc.lng });
        const {
          city = "",
          province = "",
          country = "",
          address = "",
          fullAddress = ""
        } = addressData?.objAddress || {};
        const resolvedAddress = city
          ? `${city}${province ? `, ${province}` : ""}`
          : fullAddress || address || `${loc.lat}, ${loc.lng}`;

        setDraftAddress(resolvedAddress);
        setDraftAddressDetails({
          city,
          province,
          country,
          latitude: loc.lat,
          longitude: loc.lng,
          formattedAddress: resolvedAddress
        });
      }
    } catch (err) {
      console.error("handleCurrentLocationClick error:", err);
    } finally {
      setIsGettingLocation(false);
    }
  };

  // Clear all filters inside draft state
  const handleClearAll = () => {
    setDraftCompanyType("all");
    setDraftTrade(null);
    setDraftAddress("");
    setDraftAddressDetails(null);
    setTradeSearchText("");
    appliedTradeSearchRef.current = "";
    if (tradeAccordionOpen) {
      fetchTrades("", 1, false);
    }
  };

  // Commit draft filters and close
  const handleApply = () => {
    if (onApplyFilters) {
      onApplyFilters({
        companyType: draftCompanyType,
        selectedTrade: draftTrade,
        address: draftAddress,
        addressDetails: draftAddressDetails
      });
    }
    onClose();
  };

  // Compute active badge counter
  const activeCount =
    (draftCompanyType !== "all" ? 1 : 0) +
    (draftTrade ? 1 : 0) +
    (draftAddress?.trim() ? 1 : 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)"
          }
        }
      }}
    >
      <Box
        className="user-modal-responsive"
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "88vh",
          width: { xs: "92%", sm: "520px" },
          maxWidth: "520px !important",
          borderRadius: "20px !important",
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
          p: { xs: 2.5, sm: 3 },
          bgcolor: "#ffffff",
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1.75,
            borderBottom: "1px solid #f1f5f9"
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                bgcolor: lightenHexColor(BIBlue, 94),
                color: BIBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <FilterListIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#1e293b" }}>
              {TEXT_FILTERS}
            </Typography>
            {activeCount > 0 && (
              <Chip
                label={`${activeCount} active`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  bgcolor: lightenHexColor(BIBlue, 92),
                  color: BIBlue,
                  borderRadius: "6px"
                }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {activeCount > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearAll}
                sx={{
                  textTransform: "none",
                  color: "#64748b",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1,
                  "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" }
                }}
              >
                {BUTTON_CLEARALL}
              </Button>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#f8fafc",
                color: "#64748b",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#f1f5f9", color: "#1e293b" }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Scrollable Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            py: 2,
            pr: 0.5,
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 }
          }}
        >
          {/* Section 1: Address / Location */}
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: "14px",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0"
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
              <LocationOnIcon sx={{ fontSize: 17, color: BIBlue }} />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "#475569"
                }}
              >
                Location
              </Typography>
            </Stack>

            <SearchMap
              searchTerm={draftAddress}
              setSearchTerm={setDraftAddress}
              handleSearch={handlePlaceSelect}
              onClear={handleClearAddress}
              useDetailedLabel
              placeholder="Search address or location..."
            />

            <Button
              startIcon={
                isGettingLocation ? (
                  <CircularProgress size={14} sx={{ color: BIBlue }} />
                ) : (
                  <NearMeIcon sx={{ fontSize: 15 }} />
                )
              }
              variant="text"
              size="small"
              disabled={isGettingLocation}
              onClick={handleCurrentLocationClick}
              sx={{
                mt: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                color: BIBlue,
                borderRadius: "8px",
                px: 1,
                py: 0.25,
                "&:hover": { bgcolor: lightenHexColor(BIBlue, 94) }
              }}
            >
              {isGettingLocation
                ? "Detecting location..."
                : "Use current location"}
            </Button>
          </Box>

          {/* Section 2: Company Type */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "#475569",
                mb: 1.25
              }}
            >
              {TEXT_COMPANY}
            </Typography>
            <Grid container spacing={1}>
              {[
                { value: "all", label: "All", icon: null },
                {
                  value: "companies",
                  label: "Companies",
                  icon: <BusinessIcon sx={{ fontSize: 16 }} />
                },
                {
                  value: "manufacturers",
                  label: "Manufacturers",
                  icon: <FactoryIcon sx={{ fontSize: 16 }} />
                }
              ].map((type) => {
                const isSelected = draftCompanyType === type.value;
                return (
                  <Grid size={{ xs: 4 }} key={type.value}>
                    <Box
                      onClick={() => setDraftCompanyType(type.value)}
                      sx={{
                        py: 1.1,
                        px: 1,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.6,
                        cursor: "pointer",
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: "0.82rem",
                        bgcolor: isSelected ? BIBlue : "#f8fafc",
                        color: isSelected ? "#fff" : "#334155",
                        border: "1px solid",
                        borderColor: isSelected ? BIBlue : "#e2e8f0",
                        transition: "all 0.18s ease-in-out",
                        boxShadow: isSelected
                          ? "0 4px 10px rgba(8, 38, 184, 0.22)"
                          : "none",
                        "&:hover": {
                          bgcolor: isSelected ? BIBlue : "#f1f5f9",
                          borderColor: isSelected ? BIBlue : "#cbd5e1"
                        }
                      }}
                    >
                      {type.icon}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "inherit",
                          fontWeight: "inherit",
                          color: "inherit",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {type.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Section 3: Trade Accordion */}
          <Box
            sx={{
              mb: 1,
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              overflow: "hidden",
              bgcolor: "#ffffff"
            }}
          >
            <Box
              onClick={() => setTradeAccordionOpen((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: "#f8fafc",
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": { bgcolor: "#f1f5f9" }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ConstructionIcon sx={{ fontSize: 18, color: BIBlue }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>
                  {TEXT_TRADE}
                </Typography>
                {draftTrade && (
                  <Chip
                    label={draftTrade.label || draftTrade.name}
                    size="small"
                    onDelete={(e) => {
                      e.stopPropagation();
                      setDraftTrade(null);
                    }}
                    sx={{
                      height: 22,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      bgcolor: lightenHexColor(BIBlue, 90),
                      color: BIBlue,
                      maxWidth: 180,
                      borderRadius: "6px",
                      "& .MuiChip-deleteIcon": {
                        color: BIBlue,
                        fontSize: 14,
                        "&:hover": { color: "#dc2626" }
                      }
                    }}
                  />
                )}
              </Stack>
              <IconButton size="small" sx={{ color: "#64748b" }}>
                {tradeAccordionOpen ? (
                  <ExpandLessIcon sx={{ fontSize: 20 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>

            <Collapse in={tradeAccordionOpen}>
              <Box sx={{ p: 1.5, borderTop: "1px solid #f1f5f9" }}>
                {/* Search trades row with search bar and button */}
                <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search trades..."
                    value={tradeSearchText}
                    onChange={handleTradeSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleTradeSearch();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: BIGrey, fontSize: 19 }} />
                        </InputAdornment>
                      ),
                      endAdornment: tradeSearchText ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearTradeSearch}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        bgcolor: "#f8fafc"
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleTradeSearch}
                    disabled={isTradeLoading}
                    sx={{
                      minWidth: 86,
                      width: 86,
                      height: 40,
                      p: 0,
                      borderRadius: "10px",
                      bgcolor: BIBlue,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      whiteSpace: "nowrap",
                      boxShadow: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        bgcolor: BIBlue,
                        boxShadow: "none"
                      },
                      "&.Mui-disabled": {
                        bgcolor: isTradeSearching ? lightenHexColor(BIBlue, 40) : BIBlue,
                        color: "#ffffff"
                      }
                    }}
                  >
                    {isTradeSearching ? (
                      <CircularProgress size={18} sx={{ color: "#ffffff" }} />
                    ) : (
                      BUTTON_SEARCH
                    )}
                  </Button>
                </Stack>

                {/* Paginated Trade List */}
                <Box
                  sx={{
                    maxHeight: 220,
                    overflowY: "auto",
                    border: "1px solid #f1f5f9",
                    borderRadius: "10px",
                    p: 0.5,
                    "&::-webkit-scrollbar": { width: 5 },
                    "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 }
                  }}
                >
                  {isTradeLoading && tradeList.length === 0 ? (
                    <Stack
                      sx={{
                        height: 120,
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <CircularProgress size={24} sx={{ color: BIBlue }} />
                    </Stack>
                  ) : tradeList.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                        No trades found
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {tradeList.map((trade) => {
                        const isSelected =
                          draftTrade &&
                          (draftTrade.id === trade.id ||
                            draftTrade.value === trade.value);
                        return (
                          <ListItem key={trade.id || trade.value} disablePadding>
                            <ListItemButton
                              dense
                              onClick={() => handleTradeToggle(trade)}
                              sx={{
                                py: 0.65,
                                px: 1.25,
                                borderRadius: "8px",
                                my: 0.25,
                                bgcolor: isSelected
                                  ? lightenHexColor(BIBlue, 94)
                                  : "transparent",
                                "&:hover": {
                                  bgcolor: isSelected
                                    ? lightenHexColor(BIBlue, 90)
                                    : "#f8fafc"
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Radio
                                  edge="start"
                                  checked={isSelected}
                                  size="small"
                                  disableRipple
                                  sx={{
                                    // p: 0.25,
                                    color: isSelected ? BIBlue : undefined,
                                    "&.Mui-checked": { color: BIBlue }
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={trade.label}
                                primaryTypographyProps={{
                                  fontSize: "0.84rem",
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected ? BIBlue : "text.primary"
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}

                  {/* Load more button if there are more trades */}
                  {!isTradeLoading && tradeList.length > 0 && hasMoreTrades && (
                    <Box sx={{ p: 1, textAlign: "center" }}>
                      <Button
                        size="small"
                        onClick={handleLoadMoreTrades}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: BIBlue
                        }}
                      >
                        Load more trades
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            pt: 2,
            mt: 0.5,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Button
            variant="text"
            onClick={handleClearAll}
            disabled={activeCount === 0}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.84rem",
              color: activeCount > 0 ? "#64748b" : "#cbd5e1",
              borderRadius: "10px",
              px: 1.5,
              "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" }
            }}
          >
            {BUTTON_CLEARALL}
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.88rem",
              px: 3.5,
              py: 1,
              borderRadius: "12px",
              bgcolor: BIBlue,
              boxShadow: "0 6px 16px rgba(8, 38, 184, 0.28)",
              "&:hover": {
                bgcolor: BIBlue,
                boxShadow: "0 8px 20px rgba(8, 38, 184, 0.35)"
              }
            }}
          >
            {activeCount > 0 ? `${BUTTON_APPLY} (${activeCount})` : BUTTON_APPLY}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CompanyListFilterModal;
